import { PERMISSIONS, POLICY_STATUS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { ApiError } from '../../../lib/api/errors.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { useCreatePaymentPolicy, useUpdatePaymentPolicy } from '../api/mutations.js';
import { usePaymentPolicyDetail } from '../api/queries.js';
import {
  type PaymentPolicyDetail,
  type PaymentPolicyFormInput,
  paymentPolicyFormInput,
} from '../api/schemas.js';
import { PaymentPolicyFields } from '../forms/payment-policy-fields.js';

/**
 * Creating and editing a payment policy.
 *
 * One component for both, because they are the same fields under the same
 * validation; only the defaults, the title and the mutation differ.
 */

/** 409. Named because the linter forbids a bare numeric literal here. */
const HTTP_CONFLICT = 409;

const CREATE_DEFAULTS: PaymentPolicyFormInput = {
  propertyId: '',
  code: '',
  name: '',
  description: '',
  contentHtml: '',
  status: POLICY_STATUS.ACTIVE,
};

/** Null columns become empty strings: a controlled input cannot take null. */
function defaultsFrom(policy: PaymentPolicyDetail): PaymentPolicyFormInput {
  return {
    propertyId: policy.propertyId,
    code: policy.code,
    name: policy.name,
    description: policy.description ?? '',
    contentHtml: policy.contentHtml ?? '',
    status: policy.status,
  };
}

export function PaymentPolicyFormPage(): React.JSX.Element {
  const policyId = useParams().policyId;
  const isEdit = policyId !== undefined;

  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.PAYMENT_POLICY_UPDATE : PERMISSIONS.PAYMENT_POLICY_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditPaymentPolicy policyId={policyId} /> : <CreatePaymentPolicy />}
    </PermissionGuard>
  );
}

function CreatePaymentPolicy(): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreatePaymentPolicy();

  const form = useAppForm<PaymentPolicyFormInput>({
    schema: paymentPolicyFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Payment policy created',
    onSubmit: async (values) => {
      await create.mutateAsync(values);
      void navigate('/payment-policies');
    },
  });

  return (
    <FormPage
      title="New payment policy"
      description="When a guest is charged, what deposit is taken, and what a rate plan applies."
      backTo="/payment-policies"
      backLabel="Payment policies"
      submitLabel="Create policy"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PaymentPolicyFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="create"
      />
    </FormPage>
  );
}

function EditPaymentPolicy({ policyId }: { readonly policyId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdatePaymentPolicy();
  const query = usePaymentPolicyDetail(policyId);

  if (query.isPending) return <LoadingState label="Loading payment policy" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditPaymentPolicyForm
      policy={query.data}
      onSaved={() => {
        void navigate('/payment-policies');
      }}
      save={async (values) => {
        try {
          await update.mutateAsync({ policyId, version: query.data.version, values });
        } catch (error) {
          if (error instanceof ApiError && error.status === HTTP_CONFLICT) {
            await query.refetch();
          }
          throw error;
        }
      }}
    />
  );
}

function EditPaymentPolicyForm({
  policy,
  save,
  onSaved,
}: {
  readonly policy: PaymentPolicyDetail;
  readonly save: (values: PaymentPolicyFormInput) => Promise<void>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const form = useAppForm<PaymentPolicyFormInput>({
    schema: paymentPolicyFormInput,
    defaultValues: defaultsFrom(policy),
    successMessage: 'Payment policy updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={`Edit ${policy.name}`}
      description="Changes take effect immediately for every rate plan that uses this policy."
      backTo="/payment-policies"
      backLabel="Payment policies"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PaymentPolicyFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="edit"
      />
    </FormPage>
  );
}
